import React, { useState } from "react";
import { IoIosArrowRoundForward, IoIosArrowRoundBack } from "react-icons/io";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  MapPin,
  Mail,
  User,
  MessageSquare,
  Send,
  CheckCircle,
  AlertCircle,
  Loader2,
} from "lucide-react";

const Contact = () => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState(null); // 'success', 'error', or null

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = t("contact_name_required");
    if (!formData.email) {
      newErrors.email = t("contact_email_required");
    } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) {
      newErrors.email = t("contact_email_invalid");
    }
    if (!formData.subject) newErrors.subject = t("select_subject_required");
    if (!formData.message) {
      newErrors.message = t("contact_message_required");
    } else if (formData.message.length < 10) {
      newErrors.message = t("message_min_length");
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();

    if (Object.keys(validationErrors).length === 0) {
      setIsSubmitting(true);

      try {
        // Simulating API call
        await new Promise((resolve) => setTimeout(resolve, 1500));
        console.log(formData);

        // Success
        setFormStatus("success");
        setFormData({
          name: "",
          email: "",
          subject: "",
          message: "",
        });
      } catch (error) {
        // Error
        setFormStatus("error");
      } finally {
        setIsSubmitting(false);

        // Reset form status after 5 seconds
        setTimeout(() => {
          setFormStatus(null);
        }, 5000);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  const language = i18n.language;
  const isRTL = language === "ar";

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  const statusVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, y: 20, transition: { duration: 0.3 } },
  };

  return (
    <div id="Contact" className="w-full bg-white overflow-hidden">
      {/* Map Section */}
      <div className="w-full relative h-[350px] md:h-[450px] overflow-hidden shadow-lg">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d3245.6861837910087!2d45.4281397!3d35.5614518!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x40002c2435186e87%3A0x948735cb867419c8!2sPark%20Tower!5e0!3m2!1sen!2siq!4v1727805328162!5m2!1sen!2siq"
          width="100%"
          height="100%"
          style={{ border: "0" }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="absolute inset-0"
        ></iframe>

        <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-white pointer-events-none"></div>

        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="absolute bottom-0 left-0 right-0 bg-white p-6 md:p-8 md:mx-auto md:max-w-lg md:rounded-t-3xl shadow-lg"
          dir={isRTL ? "rtl" : "ltr"}
        >
          <div className="flex items-center gap-4">
            <div className="bg-primary/20 p-3 rounded-full">
              <MapPin className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-bold text-lg">{t("location")}</h3>
              <p className="text-gray-600">
                Iraq - Sulaymaniyah - Park tower Mall - 1st floor - NO: 24
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Contact Form Section */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={containerVariants}
        className="container mx-auto py-16 md:py-24 px-5 xl:px-0"
        dir={isRTL ? "rtl" : "ltr"}
      >
        <motion.div
          variants={itemVariants}
          className="text-center mb-12 md:mb-16"
        >
          <h1 className="text-5xl md:text-6xl lg:text-7xl mb-4">
            {t("contact_get_in_touch")}
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {t("contact_us_subtitle")}
          </p>
        </motion.div>

        {/* Status Messages */}
        <AnimatePresence>
          {formStatus && (
            <motion.div
              key={formStatus}
              variants={statusVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className={`max-w-3xl mx-auto mb-8 p-4 rounded-lg ${
                formStatus === "success"
                  ? "bg-green-50 text-green-800"
                  : "bg-red-50 text-red-800"
              }`}
            >
              <div className="flex items-center gap-3">
                {formStatus === "success" ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <AlertCircle className="w-5 h-5" />
                )}
                <p>
                  {formStatus === "success"
                    ? t("contact_success_message")
                    : t("contact_error_message")}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Form */}
        <motion.form
          variants={itemVariants}
          onSubmit={handleSubmit}
          className="max-w-3xl mx-auto rounded-2xl p-6 md:p-10 bg-white shadow-lg border border-gray-100"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name Field */}
            <div className="relative">
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("contact_name")} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div
                  className={`absolute inset-y-0 ${
                    isRTL ? "right-0 pr-3" : "left-0 pl-3"
                  } flex items-center pointer-events-none`}
                >
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <motion.input
                  whileFocus={{ scale: 1.01 }}
                  transition={{ duration: 0.2 }}
                  type="text"
                  id="name"
                  name="name"
                  placeholder={t("contact_name_placeholder")}
                  value={formData.name}
                  onChange={handleChange}
                  className={`block w-full ${
                    isRTL ? "pr-10 pl-3" : "pl-10 pr-3"
                  } py-3 border ${
                    errors.name ? "border-red-500" : "border-gray-300"
                  } rounded-lg shadow-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-all duration-200`}
                />
              </div>
              {errors.name && (
                <motion.span
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-500 text-sm mt-1 block"
                >
                  {errors.name}
                </motion.span>
              )}
            </div>

            {/* Email Field */}
            <div className="relative">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("contact_email")} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div
                  className={`absolute inset-y-0 ${
                    isRTL ? "right-0 pr-3" : "left-0 pl-3"
                  } flex items-center pointer-events-none`}
                >
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <motion.input
                  whileFocus={{ scale: 1.01 }}
                  transition={{ duration: 0.2 }}
                  type="email"
                  id="email"
                  name="email"
                  placeholder={t("contact_email_placeholder")}
                  value={formData.email}
                  onChange={handleChange}
                  className={`block w-full ${
                    isRTL ? "pr-10 pl-3" : "pl-10 pr-3"
                  } py-3 border ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  } rounded-lg shadow-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-all duration-200`}
                  dir="ltr"
                />
              </div>
              {errors.email && (
                <motion.span
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-500 text-sm mt-1 block"
                >
                  {errors.email}
                </motion.span>
              )}
            </div>
          </div>

          {/* Subject Field */}
          <div className="mt-6">
            <label
              htmlFor="subject"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t("subject")} <span className="text-red-500">*</span>
            </label>
            <select
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className={`block w-full px-3 py-3 border ${
                errors.subject ? "border-red-500" : "border-gray-300"
              } rounded-lg shadow-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-all duration-200`}
            >
              <option value="">{t("select_a_subject")}</option>
              <option value="software_development">
                {t("feature_7_title")}
              </option>
              <option value="mobile_app">{t("feature_8_title")}</option>
              <option value="web_development">{t("feature_9_title")}</option>
              <option value="digital_marketing">
                {t("digital_marketing")}
              </option>
              <option value="3d_designs">{t("feature_2_title")}</option>
              <option value="api_development">{t("api_development")}</option>
              <option value="other">{t("other_services")}</option>
            </select>
            {errors.subject && (
              <motion.span
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-500 text-sm mt-1 block"
              >
                {errors.subject}
              </motion.span>
            )}
          </div>

          {/* Message Field */}
          <div className="mt-6">
            <label
              htmlFor="message"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t("contact_message")} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div
                className={`absolute top-3 ${
                  isRTL ? "right-3" : "left-3"
                } flex items-start pointer-events-none`}
              >
                <MessageSquare className="h-5 w-5 text-gray-400" />
              </div>
              <motion.textarea
                whileFocus={{ scale: 1.01 }}
                transition={{ duration: 0.2 }}
                id="message"
                name="message"
                rows="5"
                placeholder={t("contact_message_placeholder")}
                value={formData.message}
                onChange={handleChange}
                className={`block w-full ${
                  isRTL ? "pr-10 pl-3" : "pl-10 pr-3"
                } py-3 border ${
                  errors.message ? "border-red-500" : "border-gray-300"
                } rounded-lg shadow-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-all duration-200`}
              ></motion.textarea>
            </div>
            {errors.message && (
              <motion.span
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-500 text-sm mt-1 block"
              >
                {errors.message}
              </motion.span>
            )}
          </div>

          {/* Privacy Policy */}
          <div className="mt-6">
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="privacy"
                  name="privacy"
                  type="checkbox"
                  className="h-4 w-4 border-gray-300 rounded text-primary focus:ring-primary"
                />
              </div>
              <div className={`${isRTL ? "mr-3" : "ml-3"} text-sm`}>
                <label htmlFor="privacy" className="text-gray-600">
                  {t("i_agree_to")}{" "}
                  <a
                    target="_blank"
                    href="/privacy-policy"
                    className="text-primary font-medium hover:text-primary/80"
                  >
                    {t("privacy_policy")}
                  </a>
                </label>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-8">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={isSubmitting}
              className="w-full md:w-auto px-8 py-4 bg-primary text-black font-medium rounded-xl flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all duration-300"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t("sending")}
                </>
              ) : (
                <>
                  {t("contact_send_message")}
                  <span className="bg-black rounded-xl p-2">
                    {language === "en" ? (
                      <IoIosArrowRoundForward className="w-5 h-5 text-white" />
                    ) : (
                      <IoIosArrowRoundBack className="w-5 h-5 text-white" />
                    )}
                  </span>
                </>
              )}
            </motion.button>
          </div>
        </motion.form>
      </motion.div>
    </div>
  );
};

export default Contact;
